import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CheckIcon, ServerIcon } from 'lucide-react';

/* The enterprise proof points, as a bento. Sizes differ on purpose: the two
 questions that decide an enterprise deal — what it connects to, and whether
 security will approve it — get the large tiles. */

function CardShell({
  title,
  blurb,
  to,
  className,
  arrowClass,
  children
}: {
  title: string;
  blurb: string;
  to: string;
  className: string;
  arrowClass: string;
  children: React.ReactNode;
}) {
  return <article className={`flex flex-col overflow-hidden rounded-2xl p-7 md:p-8 ${className}`} data-id="element-2363" data-mp="183x9lm2r8bzl">
      <h3 className="font-title text-xl font-medium tracking-tight md:text-2xl" data-id="element-2364" data-mp="1d54d38xv6p0g">
        {title}
      </h3>
      <p className="mt-2 max-w-[42ch] leading-relaxed opacity-60" data-id="element-2365" data-mp="1i2whh2fp43lj">{blurb}</p>
      <Link to={to} aria-label={`More about ${title}`} className={`mt-5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-transform hover:scale-105 ${arrowClass}`} data-id="element-2366" data-mp="3ex9edb196ynu">
        <ArrowRightIcon size={16} strokeWidth={2.5} data-id="element-2367" data-mp="1j0xwc4mpjf7r" />
      </Link>
      <div className="mt-auto pt-9" data-id="element-2368" data-mp="1e961hhwiqnbt">{children}</div>
    </article>;
}
const ROLES = [{
  capability: 'Ask across connected tools',
  allowed: [true, true, true]
}, {
  capability: 'Create groups',
  allowed: [false, false, true]
}, {
  capability: 'Connect a new tool',
  allowed: [false, true, true]
}];
function RolesTable() {
  return <div className="rounded-xl bg-[#F4F3F1] p-4 text-[#2B211A] md:p-5" data-id="element-2369" data-mp="21xpe29mlpm9p">
      <div className="grid grid-cols-[minmax(0,1fr)_repeat(3,60px)] items-center gap-y-4" data-id="element-2370" data-mp="1wzxcenw5mlsb">
        <span className="text-[13px] font-bold" data-id="element-2371" data-mp="1sxtw9lyjit0p">Roles</span>
        {['Member', 'Editor', 'Admin'].map(r => <span key={r} className="justify-self-center rounded-full bg-white px-2.5 py-1 text-[11.5px] font-semibold" data-id="element-2372" data-mp="1nz966fd05ppz">
            {r}
          </span>)}

        {ROLES.map(row => <React.Fragment key={row.capability} data-id="element-2373" data-mp="hgu6oy3kusad">
            <span className="pr-3 text-[12.5px] text-neutral-600" data-id="element-2374" data-mp="cjki1ko0iknl">
              {row.capability}
            </span>
            {row.allowed.map((ok, i) => <span key={i} className="justify-self-center" data-id="element-2375" data-mp="dywohetlfmvm" data-mpi={i}>
                {ok ? <span className="flex h-[18px] w-[18px] items-center justify-center rounded bg-[#2B211A]" data-id="element-2376" data-mp="iz13f0eus9jq">
                    <CheckIcon size={12} strokeWidth={3.5} className="text-white" data-id="element-2377" data-mp="4zrqqapuvxki" />
                  </span> : <span className="block h-[18px] w-[18px] rounded border border-neutral-300 bg-white" data-id="element-2378" data-mp="90le92zdzacv" />}
              </span>)}
          </React.Fragment>)}
      </div>
    </div>;
}
const CERTS = ['SOC 2 Type II', 'GDPR', 'HIPAA', 'SSO / SAML'];
const MODELS = [{
  domain: 'openai.com',
  label: 'OpenAI'
}, {
  domain: 'anthropic.com',
  label: 'Anthropic'
}, {
  domain: 'gemini.google.com',
  label: 'Gemini'
}, {
  domain: 'mistral.ai',
  label: 'Mistral'
}, {
  domain: 'meta.com',
  label: 'Llama'
}, {
  domain: 'deepseek.com',
  label: 'DeepSeek'
}];
const CLOUDS = [{
  domain: 'cloud.google.com',
  label: 'Google Cloud'
}, {
  domain: 'azure.microsoft.com',
  label: 'Azure'
}, {
  domain: 'aws.amazon.com',
  label: 'AWS'
}];
const PROTOCOLS = ['MCP', 'A2A', 'API'];
const SURFACES = [{
  domain: 'slack.com',
  label: 'Slack'
}, {
  domain: 'microsoft.com',
  label: 'Teams'
}, {
  domain: 'openai.com',
  label: 'ChatGPT'
}, {
  domain: 'getgiga.com',
  label: 'Web'
}];
const STACK = ['slack.com', 'salesforce.com', 'hubspot.com', 'github.com', 'notion.so', 'atlassian.com', 'google.com', 'zoom.us', 'stripe.com', 'linear.app', 'dropbox.com', 'figma.com'];
export function PlatformBento() {
  return <section className="px-6 py-20 md:px-10 md:py-28" data-id="element-2379" data-mp="37tattpaq4ezq">
      <div className="mx-auto max-w-[1100px]" data-id="element-2380" data-mp="3miqkz6e62mfy">
        <h2 className="mx-auto max-w-[18ch] text-center font-title text-3xl font-medium leading-tight tracking-tight md:text-5xl" data-id="element-2381" data-mp="3migvdjx1ahas">
          The all-in-one platform to adopt AI as an organisation.
        </h2>

        <div className="mt-14 grid gap-5 lg:grid-cols-3" data-id="element-2382" data-mp="3vjy6sozhxut5">
          {/* Connected — the widest tile, because it is the first question. */}
          <CardShell title="Connected to your stack" blurb="Giga answers from the systems you already run on, not from a copy of them." to="/integrations" className="bg-[#D5DCDA] text-[#2B211A] lg:col-span-2" arrowClass="bg-black/10 text-[#2B211A]" data-id="element-2383" data-mp="1ovo6pk16hh8d">
            <div className="grid grid-cols-6 gap-2.5" data-id="element-2384" data-mp="1ym0xvv3i08nh">
              {STACK.map(d => <span key={d} className="flex h-12 items-center justify-center rounded-lg bg-white/70" data-id="element-2385" data-mp="22lrifh2flsjk">
                  <img src={`https://www.google.com/s2/favicons?domain=${d}&sz=64`} alt="" aria-hidden="true" className="h-5 w-5 rounded object-contain" data-id="element-2386" data-mp="15xli36cjlmb3" />
                </span>)}
            </div>
          </CardShell>

          <CardShell title="Security-first" blurb="Audited, compliant, and never trained on your data." to="/unlisted/security" className="bg-[#1A1512] text-white" arrowClass="bg-white/15 text-white" data-id="element-2387" data-mp="1evowlip41ite">
            <ul className="space-y-2.5" data-id="element-2388" data-mp="2qbvqc0mnkegd">
              {CERTS.map(c => <li key={c} className="flex items-center gap-3 rounded-lg border border-white/10 px-3.5 py-2.5 text-[13.5px] font-medium" data-id="element-2389" data-mp="2vayqb6nrzuhh">
                  <CheckIcon size={14} strokeWidth={3} className="shrink-0 text-[#FDBA74]" data-id="element-2390" data-mp="35ewze9gyvez4" />
                  {c}
                </li>)}
            </ul>
          </CardShell>

          <CardShell title="Works where your team works" blurb="No new app to adopt. Giga lives in the places work already happens." to="/company-brain" className="bg-[#1F3A3D] text-white" arrowClass="bg-white/15 text-white" data-id="element-2391" data-mp="2vok900mh0rl2">
            <ul className="grid grid-cols-2 gap-2.5" data-id="element-2392" data-mp="2mq8lw8ktcbxd">
              {SURFACES.map(s => <li key={s.label} className="flex items-center gap-2.5 rounded-lg bg-white/10 px-3 py-2.5 text-[13px] font-medium" data-id="element-2393" data-mp="2hosxc6jffna8">
                  <img src={`https://www.google.com/s2/favicons?domain=${s.domain}&sz=64`} alt="" aria-hidden="true" className="h-4 w-4 rounded object-contain" data-id="element-2394" data-mp="2csu527uz9l4j" />
                  {s.label}
                </li>)}
            </ul>
          </CardShell>

          {/* Governance — the second deal question, so it gets the wide slot. */}
          <CardShell title="Enterprise-ready" blurb="Central governance and admin controls, set once per group and inherited everywhere." to="/unlisted/enterprise" className="bg-[#3D2F25] text-white lg:col-span-2" arrowClass="bg-white/15 text-white" data-id="element-2395" data-mp="20gs3src3q8kt">
            <RolesTable data-id="element-2396" data-mp="1rhetjwg5lfz1" />
          </CardShell>

          {/* Model-agnostic — the buyer's hedge against betting on one lab. */}
          <CardShell title="Model-agnostic" blurb="Route each task to the best model, and swap them without rebuilding anything." to="/developers" className="bg-[#E3DCCE] text-[#2B211A]" arrowClass="bg-black/10 text-[#2B211A]" data-id="element-2397" data-mp="1vf2ogzhuj8qx">
            <div className="grid grid-cols-3 gap-2.5" data-id="element-2398" data-mp="2a6uv8n3kreh1">
              {MODELS.map(m => <span key={m.label} className="flex flex-col items-center gap-2 rounded-lg bg-white/70 px-2 py-3" data-id="element-2399" data-mp="2a8o4ma5kocsq">
                  <img src={`https://www.google.com/s2/favicons?domain=${m.domain}&sz=64`} alt="" aria-hidden="true" className="h-5 w-5 rounded object-contain" data-id="element-2400" data-mp="yrfwpevpwejt" />
                  <span className="text-[11px] font-semibold text-[#2B211A]/70" data-id="element-2401" data-mp="13rkd74wml9a1">
                    {m.label}
                  </span>
                </span>)}
            </div>
          </CardShell>

          <CardShell title="Deployable anywhere" blurb="Flexible options for cloud or on-premise." to="/unlisted/security" className="bg-[#1F3A3D] text-white" arrowClass="bg-white/15 text-white" data-id="element-2402" data-mp="r7drhfpsjare">
            <ul className="space-y-2.5" data-id="element-2403" data-mp="1094s465h4ree">
              {CLOUDS.map(c => <li key={c.label} className="flex items-center gap-3 rounded-lg bg-white/10 px-3.5 py-2.5 text-[13px] font-medium" data-id="element-2404" data-mp="chzgy4ahanaf">
                  <img src={`https://www.google.com/s2/favicons?domain=${c.domain}&sz=64`} alt="" aria-hidden="true" className="h-4 w-4 rounded object-contain" data-id="element-2405" data-mp="7l8xd6zgt9k9" />
                  {c.label}
                </li>)}
              <li className="flex items-center gap-3 rounded-lg border border-dashed border-white/25 px-3.5 py-2.5 text-[13px] font-medium text-white/70" data-id="element-2406" data-mp="hfr5qpftttae">
                <ServerIcon size={15} strokeWidth={1.75} className="shrink-0" data-id="element-2407" data-mp="3kzcbs46wnzqm" />
                On-premise
              </li>
            </ul>
          </CardShell>

          <CardShell title="Interoperability built-in" blurb="Open standards for custom tools, agents, and interfaces." to="/developers" className="bg-[#3F4744] text-white" arrowClass="bg-white/15 text-white" data-id="element-2408" data-mp="34ecd94cs8wtk">
            <div className="flex flex-wrap gap-2.5" data-id="element-2409" data-mp="3ec8nzv1t5pr5">
              {PROTOCOLS.map(p => <span key={p} className="rounded-full border border-white/40 px-4 py-1.5 font-title text-base font-medium" data-id="element-2410" data-mp="3eafhcfr735j5">
                  {p}
                </span>)}
            </div>
          </CardShell>
        </div>
      </div>
    </section>;
}